import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Layout, Tabs, Tooltip, Upload, message } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { HasPermission, hasObjectPermission, hasPermission } from '../../../authorization/components/HasPermission';
import { AppPermissions } from '../../../authorization/Permissions';
import AuthContext from '../../../authorization/AuthContext';
import { Utils } from '../../../common/services/Utils';
import {
    ApplicationDefinitionConditionalEditStore,
    ApplicationDefinitionConditionalBindingPreviewStore
} from '../../stores';
import {
    ConditionsRenderer,
    InputGroupRenderer,
    CreateEditInputGroupDialog,
    ConditionsImportModal,
    InputGroupTabHeader,
    AdditionalClassificationsRenderer
} from '..';
import { ProjectApplicationDefinitionEditVisualStore } from '../../../iota_applications/stores';
import { FieldSettingsDrawer } from '../../../iota_applications/components';
import LayoutHeader from '../../../../components/LayoutHeader';
import './ApplicationDefinitionConditionalEditor.less';
import TabImportDialogs from './TabImportDialogs';
import TabActions from './TabActions';
import { RcFile, UploadChangeParam } from 'antd/lib/upload';
import { Prompt } from 'react-router';
import { getInvalidBindingNavigation } from '../../misc/invalidBindingNavigation';

interface Props {
    store: ApplicationDefinitionConditionalEditStore;
    previewStore: ApplicationDefinitionConditionalBindingPreviewStore;
    applicationDefinitionEditStore: ProjectApplicationDefinitionEditVisualStore;
}

const ApplicationDefinitionConditionalEditor: React.FC<Props> = ({
    store,
    previewStore,
    applicationDefinitionEditStore
}) => {
    const authContext = React.useContext(AuthContext);

    const [invalidTarget, setInvalidTarget] = React.useState<{
        tabKey: string;
        inputId: string;
        bindingIndex: number;
    }>();

    React.useEffect(() => {
        store.setIsDataChanged(false);
        return () => {
            store.tabImport.dispose();
            store.setIsDataChanged(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        function beforeUnload(e: BeforeUnloadEvent) {
            if (!store.isDataChanged) {
                return;
            }
            e.preventDefault();
        }

        window.addEventListener('beforeunload', beforeUnload);

        return () => {
            window.removeEventListener('beforeunload', beforeUnload);
        };
    }, [store.isDataChanged]);

    if (!store.project || !store.applicationDefinition) {
        return null;
    }

    const canEdit =
        hasPermission(authContext.permissions, AppPermissions.CanEditIotaApplications) ||
        hasObjectPermission(authContext.objectPermissions, store.project.id, AppPermissions.CanEditIotaApplications);
    const activeTab = canEdit || store.activeTab !== 'additional-classifications' ? store.activeTab : 'conditions';

    React.useEffect(() => {
        store.setCanEditAdditionalClassifications(canEdit);
    }, [canEdit, store]);

    const getLastUpdateTimeLabel = () => {
        const dataIsEmpty =
            !store.applicationDefinition?.lastUpdatedBy && !store.applicationDefinition?.lastUpdatedTime;
        if (!store.project || !store.applicationDefinition || dataIsEmpty) {
            // store.isLoadingUsers
            return null;
        }

        const updatedBy = store.getUserName(store.applicationDefinition.lastUpdatedBy ?? '');
        const updatedAt = Utils.formatDateStringShort(store.applicationDefinition.lastUpdatedTime);

        return (
            <>
                Updated at: {updatedAt} by {updatedBy}
            </>
        );
    };

    const onFileChange = (info: UploadChangeParam) => {
        const status = info.file.status;
        if (status === 'done') {
            const uploadResponse = info.file.response;
            store.handleImportResponse(uploadResponse);
        } else if (status === 'error') {
            console.log(info.file.response);
            if (info.file?.response?.status === 409) {
                message.warning(info.file.response.title);
                return;
            }
            message.error(`${info.file.name} file upload failed.`);
        }
    };

    const handleBeforeUpload = async (file: RcFile) => {
        if (!file.name.endsWith('.iota_conditions')) {
            message.error('Invalid file format. Please upload a .iota_conditions file');
            return Promise.reject();
        }
        store.setCurrentImportFile(file);
        return store.setHeaders();
    };

    const handleCancel = () => {
        store.setIsDataChanged(false);
        store.navigateToList();
    };

    const handleSave = () => {
        const navigation = getInvalidBindingNavigation(store.inputGroups);
        if (navigation) {
            navigation.inputGroup.setOpenBindingGroupIds(navigation.openBindingGroupIds);
            navigation.childGroups.forEach(({ group, openIds }) => group.setOpenChildBindingGroups(openIds));
            store.setActiveTab(navigation.target.tabKey);
            setInvalidTarget(navigation.target);
            return;
        }

        setInvalidTarget(undefined);
        store.updateData();
    };

    return (
        <Layout>
            <LayoutHeader
                title={store.applicationDefinition.name}
                subtitle={Utils.getSubtitle(store.project)}
                buttons={[
                    <span key="updated-data" style={{ marginRight: 12, color: '#9BA0AA' }}>
                        {getLastUpdateTimeLabel()}
                    </span>,
                    <Tooltip key="export-button-container" title="Export bindings">
                        <button
                            type="button"
                            data-id="button-Export"
                            className="headerButton rule-export application-definition-bindings-export"
                            aria-label="Export bindings"
                            onClick={() => store.exportAppBindings()}
                        >
                            <i className="alpha-icon xs arrow-up" style={{ verticalAlign: 'text-top' }} aria-hidden />
                        </button>
                    </Tooltip>,
                    <HasPermission
                        key="application-definition-conditional-import-permission"
                        entityId={store.project.id}
                        permissionClaim={AppPermissions.CanEditIotaApplications}
                    >
                        <Tooltip key="file-uploader" title="Import bindings">
                            <Upload
                                showUploadList={false}
                                className="headerButton"
                                name="file"
                                onChange={onFileChange}
                                action={store.importBindingsActionUrl}
                                headers={store.fileImportActionHeaders}
                                beforeUpload={file => handleBeforeUpload(file)}
                                disabled={store.isUpdating || store.tabImport.isBusy}
                            >
                                <span
                                    data-id="button-Import"
                                    aria-label="Import bindings"
                                    aria-disabled={store.isUpdating || store.tabImport.isBusy}
                                >
                                    <i
                                        className="alpha-icon xs arrow-down"
                                        style={{ verticalAlign: 'text-top' }}
                                        aria-hidden
                                    />
                                </span>
                            </Upload>
                        </Tooltip>
                    </HasPermission>,
                    <Tooltip key="application-definition-conditional-field-settings-tooltip" title="Field Settings">
                        <span
                            data-id="application-definition-conditional-field-settings"
                            className="headerButton"
                            onClick={() => applicationDefinitionEditStore.openFieldSettingsDrawer(canEdit)}
                        >
                            <SettingOutlined />
                        </span>
                    </Tooltip>,
                    <Button
                        key="application-definition-conditional-preview-app"
                        data-id="application-definition-conditional-preview-app"
                        onClick={() => previewStore.previewApplication()}
                        loading={previewStore.isLoading}
                        disabled={previewStore.isLoading}
                        htmlType="button"
                        className="light"
                        size="large"
                    >
                        Preview Application
                    </Button>,
                    <HasPermission
                        key="application-definition-conditional-add-input-group-permission"
                        entityId={store.project.id}
                        permissionClaim={AppPermissions.CanEditIotaApplications}
                    >
                        <Button
                            data-id="application-definition-conditional-add-input-group"
                            onClick={() => store.setCreateEditInputGroupDialogVisible(true)}
                            htmlType="button"
                            className="light"
                            size="large"
                        >
                            Add Input Group
                        </Button>
                    </HasPermission>,
                    <Button
                        key="application-definition-conditional-cancel-editing"
                        data-id="application-definition-conditional-cancel-editing"
                        onClick={handleCancel}
                        htmlType="button"
                        className="light"
                        size="large"
                    >
                        Cancel
                    </Button>,
                    <HasPermission
                        key="application-definition-conditional-save"
                        entityId={store.project.id}
                        permissionClaim={AppPermissions.CanEditIotaApplications}
                        yes={() => (
                            <Button
                                data-id="application-definition-conditional-save"
                                type="primary"
                                size="large"
                                onClick={handleSave}
                                loading={store.isUpdating}
                                disabled={store.isUpdating || store.tabImport.isBusy}
                            >
                                Save changes
                            </Button>
                        )}
                        no={() => (
                            <Button
                                data-id="application-definition-conditional-save"
                                type="primary"
                                size="large"
                                disabled
                            >
                                Save changes
                            </Button>
                        )}
                    />
                ]}
            />
            <Layout.Content className="application-definition-conditional-editor">
                <CreateEditInputGroupDialog store={store} />
                <ConditionsImportModal store={store} />
                <TabImportDialogs store={store} />
                <FieldSettingsDrawer
                    store={applicationDefinitionEditStore}
                    isVisible={applicationDefinitionEditStore.isFieldSettingsDrawerOpen}
                    isDisabled={applicationDefinitionEditStore.isInputDisabled || !canEdit}
                    onClose={applicationDefinitionEditStore.closeFieldSettingsDrawer}
                    onFieldSettingsChange={() => store.setIsDataChanged(true)}
                />
                <Prompt
                    when={store.isDataChanged}
                    message="You have unsaved changes. Are you sure you want to leave?"
                />
                <Tabs activeKey={activeTab} onChange={store.setActiveTab}>
                    <Tabs.TabPane tab={<span style={{ userSelect: 'none' }}>Conditions</span>} key="conditions">
                        <ConditionsRenderer
                            store={store}
                            applicationDefinitionEditStore={applicationDefinitionEditStore}
                            tabActions={<TabActions store={store} canEdit={canEdit} activeTab="conditions" />}
                        />
                    </Tabs.TabPane>

                    {canEdit ? (
                        <Tabs.TabPane
                            tab={<span className="application-setup-tab-header">Additional Classifications</span>}
                            key="additional-classifications"
                        >
                            <AdditionalClassificationsRenderer
                                store={store}
                                canEdit={canEdit}
                                tabActions={
                                    <TabActions
                                        store={store}
                                        canEdit={canEdit}
                                        activeTab="additional-classifications"
                                    />
                                }
                            />
                        </Tabs.TabPane>
                    ) : null}

                    {store.inputGroups.map(inputGroup => (
                        <Tabs.TabPane key={inputGroup.tabKey} tab={<InputGroupTabHeader inputGroup={inputGroup} />}>
                            <InputGroupRenderer
                                store={store}
                                previewStore={previewStore}
                                inputGroup={inputGroup}
                                applicationDefinitionEditStore={applicationDefinitionEditStore}
                                invalidBinding={invalidTarget?.tabKey === inputGroup.tabKey ? invalidTarget : undefined}
                                tabActions={
                                    <TabActions store={store} canEdit={canEdit} activeTab={inputGroup.tabKey} />
                                }
                            />
                        </Tabs.TabPane>
                    ))}
                </Tabs>
            </Layout.Content>
        </Layout>
    );
};

export default observer(ApplicationDefinitionConditionalEditor);
